/*
 * Founding Partner access indicator + end-of-period continuation prompt.
 * Renders a subtle "Founding Partner Access — N days remaining" pill in the
 * portal chrome, escalates to a gentle amber notice at <=14 days, and shows an
 * Executive Clarity continuation modal once the 60-day period has ended.
 * Used by the School workspace and the Portfolio dashboard.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const FoundingPartnerAccess = ({ headers = {}, withCredentials = false, compact = false }) => {
  const [fp, setFp] = useState(null);
  const [busy, setBusy] = useState("");
  const [declined, setDeclined] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/founding-partner/status`, { headers, withCredentials });
      setFp(r.data);
    } catch {
      setFp({ is_fp: false });
    }
  }, [JSON.stringify(headers), withCredentials]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!fp || !fp.is_fp) return null;

  const cont = async () => {
    setBusy("continue");
    try {
      const { data } = await axios.post(`${API}/api/founding-partner/continue`, {}, { headers, withCredentials });
      toast.success("Thank you — starting your paid subscription. Your work is preserved.");
      if (data?.redirect) window.location.assign(data.redirect);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start conversion"); }
    finally { setBusy(""); }
  };
  const notYet = async () => {
    setBusy("decline");
    try {
      await axios.post(`${API}/api/founding-partner/decline`, {}, { headers, withCredentials });
      setDeclined(true);
      toast.success("No problem — your data is safe. You can continue any time.");
    } catch { /* ignore */ } finally { setBusy(""); }
  };

  const days = fp.days_remaining ?? 0;

  // Subtle in-chrome indicator (Active / Expiring Soon)
  const Indicator = () => {
    if (fp.converted_at) return null;
    if (fp.expired) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#fca5a5] bg-[#fef2f2] px-2.5 py-0.5 text-[11px] font-semibold text-[#991b1b]" data-testid="fp-access-indicator">
          Founding Partner access ended
        </span>
      );
    }
    if (fp.expiring_soon) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#fcd34d] bg-[#fffbeb] px-2.5 py-0.5 text-[11px] font-semibold text-[#92400e]" data-testid="fp-access-indicator">
          <Sparkles className="h-3 w-3" /> Your Founding Partner access ends in {days} day{days === 1 ? "" : "s"}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-2.5 py-0.5 text-[11px] font-semibold text-[#1e40af]" data-testid="fp-access-indicator">
        <Sparkles className="h-3 w-3" /> Founding Partner Access — {days} day{days === 1 ? "" : "s"} remaining
      </span>
    );
  };

  return (
    <>
      <Indicator />

      {/* Executive Clarity continuation prompt — shown once the period has ended */}
      <Dialog open={!!fp.needs_decision && !declined} onOpenChange={() => {}}>
        <DialogContent className="max-w-lg bg-white p-0 overflow-hidden" data-testid="fp-continue-modal">
          <div className="bg-[#0b1220] px-7 py-6 text-white">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#93c5fd] font-semibold">
              <Sparkles className="h-3 w-3" /> Founding Partner
            </div>
            <h2 className="mt-3 text-2xl font-bold leading-tight">Continue with MySchoolRx?</h2>
          </div>
          <div className="px-7 py-6">
            <p className="text-[14px] leading-relaxed text-[#374151]">
              Thank you for helping shape MySchoolRx as a Founding Partner. Your 60-day
              complimentary access period has ended.
            </p>
            <p className="mt-3 text-[14px] leading-relaxed text-[#374151]">
              We hope the platform has provided meaningful insight into your school or organization. If you would like
              to continue using MySchoolRx, you can now transition to a paid subscription
              <b> without losing any of the work you completed</b> during the Founding Partner period.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={cont}
                disabled={!!busy}
                className="flex-1 h-11 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold"
                data-testid="fp-continue-paid"
              >
                {busy === "continue" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue as a Paid Subscriber"}
              </Button>
              <Button
                onClick={notYet}
                disabled={!!busy}
                variant="outline"
                className="flex-1 h-11 border-[#d1d5db] text-[#374151]"
                data-testid="fp-continue-notyet"
              >
                {busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Not Yet"}
              </Button>
            </div>
            <p className="mt-4 text-[12px] text-[#6b7280]">
              Your Diagnostics, reports, schools, users and feedback are always preserved. You can return and continue any time.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FoundingPartnerAccess;
